/**
 * PrintNode React Query hooks — uzaktan yazdırma.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

export function usePrintNodeConfig() {
    return useQuery({
        queryKey: ['printnode-config'],
        queryFn: async () => {
            const { data } = await axios.get(`${API}/printnode/config`, { headers: authHeaders() });
            return data;
        },
        staleTime: 60_000,
    });
}

export function useSavePrintNodeConfig() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ apiKey, defaultPrinterId, defaultPrinterName }) => {
            const { data } = await axios.post(
                `${API}/printnode/config`,
                {
                    api_key: apiKey,
                    default_printer_id: defaultPrinterId,
                    default_printer_name: defaultPrinterName,
                },
                { headers: authHeaders() },
            );
            return data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['printnode-config'] });
            qc.invalidateQueries({ queryKey: ['printnode-printers'] });
        },
    });
}

export function useDeletePrintNodeConfig() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const { data } = await axios.delete(`${API}/printnode/config`, { headers: authHeaders() });
            return data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['printnode-config'] });
        },
    });
}

export function usePrintNodePrinters({ enabled = true } = {}) {
    return useQuery({
        queryKey: ['printnode-printers'],
        queryFn: async () => {
            const { data } = await axios.get(`${API}/printnode/printers`, { headers: authHeaders() });
            return data;  // {printers: [...], default_printer_id}
        },
        enabled,
        staleTime: 30_000,
    });
}

export function usePrintNodePrinterCapabilities(printerId, { enabled = true } = {}) {
    return useQuery({
        queryKey: ['printnode-printer-caps', printerId],
        queryFn: async () => {
            const { data } = await axios.get(
                `${API}/printnode/printers/${printerId}/capabilities`,
                { headers: authHeaders() },
            );
            return data;  // {papers:[{name,width_mm,height_mm}], medias, dpis, bins, saved_prefs}
        },
        enabled: enabled && !!printerId,
        staleTime: 5 * 60_000,
    });
}

export function usePrintNodeSubmit() {
    return useMutation({
        mutationFn: async ({
            pdfBlob, printerId, title, copies = 1,
            paper = '', fitToPage = true, rotate = 0, autoRotate = true,
            dpi = '', bin = '', useDriverDefaults = false,
        }) => {
            const fd = new FormData();
            fd.append('pdf', pdfBlob, `${title || 'print'}.pdf`);
            fd.append('printer_id', String(printerId));
            fd.append('title', title || 'Flowbil Print');
            fd.append('copies', String(copies));
            if (paper) fd.append('paper', paper);
            fd.append('fit_to_page', fitToPage ? 'true' : 'false');
            if (rotate) fd.append('rotate', String(rotate));
            fd.append('auto_rotate', autoRotate ? 'true' : 'false');
            fd.append('use_driver_defaults', useDriverDefaults ? 'true' : 'false');
            if (dpi) fd.append('dpi', dpi);
            if (bin) fd.append('bin', bin);
            const { data } = await axios.post(`${API}/printnode/print`, fd, {
                headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
            });
            return data;
        },
    });
}
